import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Star, 
  ShoppingBag, 
  Clock,
  Repeat,
  Award,
  Share2,
  DollarSign,
  ArrowUpRight,
} from "lucide-react";
import { SocialMediaAnalytics } from "@/components/partner/SocialMediaAnalytics";
import PartnerLayout from "./PartnerLayout";

interface SalesStats {
  totalOrders: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersThisMonth: number;
  revenueThisMonth: number;
  monthOverMonthGrowth: number;
}

interface LoyaltyStats {
  totalCustomers: number;
  repeatCustomers: number;
  repeatRate: number;
  vipCustomers: number;
  averageVisitsPerCustomer: number;
}

interface PeakHour {
  hour: number;
  orders: number;
  percentage: number;
}

interface MenuSentiment {
  itemName: string;
  mentions: number;
  positivePercentage: number;
  sentiment: 'positive' | 'neutral' | 'negative';
}

export default function PartnerPerformance() {
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  
  const { data: salesStats, isLoading: salesLoading } = useQuery<SalesStats>({
    queryKey: ['/api/partner/analytics/sales', restaurantId],
    enabled: !!restaurantId,
  });
  
  const { data: loyaltyStats, isLoading: loyaltyLoading } = useQuery<LoyaltyStats>({
    queryKey: ['/api/partner/analytics/loyalty', restaurantId],
    enabled: !!restaurantId,
  });

  const { data: peakHours, isLoading: peakLoading } = useQuery<PeakHour[]>({
    queryKey: ['/api/partner/analytics/peak-hours', restaurantId],
    enabled: !!restaurantId,
  });

  const { data: menuSentiment, isLoading: sentimentLoading } = useQuery<MenuSentiment[]>({
    queryKey: ['/api/partner/analytics/menu-sentiment', restaurantId],
    enabled: !!restaurantId,
  });
  
  const formatHour = (hour: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h = hour % 12 || 12;
    return `${h}${ampm}`;
  };
  
  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-performance-title">Performance</h1>
          <p className="text-muted-foreground">See how your posts drive orders and customer engagement</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {salesLoading ? <Skeleton className="h-12 w-full" /> : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <DollarSign className="h-3.5 w-3.5 text-green-500" />
                    <span className="text-xs text-muted-foreground">Revenue</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-perf-revenue">
                    ${(salesStats?.revenueThisMonth || 0).toLocaleString()}
                  </div>
                  <div className="flex items-center gap-0.5 text-xs mt-0.5">
                    {(salesStats?.monthOverMonthGrowth || 0) >= 0 ? (
                      <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-red-500" />
                    )}
                    <span className={salesStats?.monthOverMonthGrowth && salesStats.monthOverMonthGrowth >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {Math.abs(salesStats?.monthOverMonthGrowth || 0).toFixed(1)}%
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {salesLoading ? <Skeleton className="h-12 w-full" /> : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <ShoppingBag className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Orders</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-perf-orders">
                    {(salesStats?.ordersThisMonth || 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">this month</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {loyaltyLoading ? <Skeleton className="h-12 w-full" /> : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <Repeat className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Repeat</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-perf-repeat">
                    {(loyaltyStats?.repeatRate || 0).toFixed(0)}%
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{loyaltyStats?.repeatCustomers || 0} customers</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-4 pb-3 px-3">
              {salesLoading ? <Skeleton className="h-12 w-full" /> : (
                <>
                  <div className="flex items-center gap-1 mb-0.5">
                    <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Avg Order</span>
                  </div>
                  <div className="text-xl font-bold" data-testid="text-perf-aov">
                    ${(salesStats?.averageOrderValue || 0).toFixed(2)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">per order</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="social">
          <TabsList>
            <TabsTrigger value="social" data-testid="tab-social">
              <Share2 className="h-4 w-4 mr-1" />
              Social
            </TabsTrigger>
            <TabsTrigger value="sales" data-testid="tab-sales">
              <ShoppingBag className="h-4 w-4 mr-1" />
              Sales
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers">
              <Users className="h-4 w-4 mr-1" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="menu" data-testid="tab-menu">
              <Star className="h-4 w-4 mr-1" />
              Menu
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="social" className="mt-4">
            {restaurantId && (
              <SocialMediaAnalytics restaurantId={restaurantId} />
            )}
          </TabsContent>

          <TabsContent value="sales" className="mt-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Peak Hours</CardTitle>
                <CardDescription>When customers order the most</CardDescription>
              </CardHeader>
              <CardContent>
                {peakLoading ? (
                  <Skeleton className="h-32 w-full" />
                ) : peakHours && peakHours.length > 0 ? (
                  <div className="flex items-end gap-1 h-32">
                    {peakHours.map((hour) => (
                      <div 
                        key={hour.hour} 
                        className="flex-1 flex flex-col items-center gap-1"
                        title={`${formatHour(hour.hour)}: ${hour.orders} orders`}
                      >
                        <div 
                          className="w-full bg-primary/20 rounded-t transition-all hover-elevate"
                          style={{ height: `${hour.percentage}%`, minHeight: '4px' }}
                        />
                        <span className="text-[10px] text-muted-foreground">{formatHour(hour.hour)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No order data available</p>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                  <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {salesLoading ? <Skeleton className="h-8 w-24" /> : (
                    <>
                      <div className="text-2xl font-bold">{(salesStats?.totalOrders || 0).toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground">+{salesStats?.ordersThisMonth || 0} this month</p>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                  {salesLoading ? <Skeleton className="h-8 w-24" /> : (
                    <div className="text-2xl font-bold">${(salesStats?.totalRevenue || 0).toLocaleString()}</div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Order Value</CardTitle>
                  <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {salesLoading ? <Skeleton className="h-8 w-24" /> : (
                    <div className="text-2xl font-bold">${(salesStats?.averageOrderValue || 0).toFixed(2)}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="customers" className="mt-4 space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? <Skeleton className="h-8 w-20" /> : (
                    <div className="text-2xl font-bold">{(loyaltyStats?.totalCustomers || 0).toLocaleString()}</div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Repeat Customers</CardTitle>
                  <Repeat className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? <Skeleton className="h-8 w-20" /> : (
                    <>
                      <div className="text-2xl font-bold">{(loyaltyStats?.repeatCustomers || 0).toLocaleString()}</div>
                      <p className="text-xs text-muted-foreground">{(loyaltyStats?.repeatRate || 0).toFixed(1)}% repeat rate</p>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">VIP Customers</CardTitle>
                  <Award className="h-4 w-4 text-yellow-500" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? <Skeleton className="h-8 w-20" /> : (
                    <div className="text-2xl font-bold">{(loyaltyStats?.vipCustomers || 0).toLocaleString()}</div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Visits</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {loyaltyLoading ? <Skeleton className="h-8 w-20" /> : (
                    <div className="text-2xl font-bold">{(loyaltyStats?.averageVisitsPerCustomer || 0).toFixed(1)}</div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="menu" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Menu Item Sentiment</CardTitle>
                <CardDescription>What customers are saying about your dishes</CardDescription>
              </CardHeader>
              <CardContent>
                {sentimentLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : menuSentiment && menuSentiment.length > 0 ? (
                  <div className="space-y-3">
                    {menuSentiment.map((item) => (
                      <div 
                        key={item.itemName} 
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{item.itemName}</p>
                          <p className="text-xs text-muted-foreground">{item.mentions} mentions in reviews</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                item.sentiment === 'positive' ? 'bg-green-500' :
                                item.sentiment === 'negative' ? 'bg-red-500' : 'bg-yellow-500'
                              }`}
                              style={{ width: `${item.positivePercentage}%` }}
                            />
                          </div>
                          <Badge variant={
                            item.sentiment === 'positive' ? 'default' :
                            item.sentiment === 'negative' ? 'destructive' : 'secondary'
                          }>
                            {item.positivePercentage}% positive
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    Not enough review data to analyze menu sentiment yet
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  );
}
